import { useEffect, useRef } from 'react'

import { useCoAgent } from '@copilotkit/react-core'

import type { EmployeeWithMeetings } from 'modules/meetings/lib/types/chat'

interface AiAgentStateSyncProps {
  agent: string
  meetingId: number
  managedEmployees: EmployeeWithMeetings[]
  peers: EmployeeWithMeetings[]
}

/**
 * Component that syncs meeting state to the AI agent using CopilotKit's useCoAgent.
 * This component should only be rendered when AI is enabled (CopilotKit provider is present).
 */
export const AiAgentStateSync = ({
  agent,
  meetingId,
  managedEmployees,
  peers,
}: AiAgentStateSyncProps) => {
  const { setState } = useCoAgent({
    name: agent,
    initialState: {
      meetingId,
      managedEmployees,
      peers,
      platform: 'web',
    },
  })

  const prevValuesRef = useRef({
    meetingId: -1, // Start with invalid value to force initial sync
    managedEmployees: '',
    peers: '',
  })

  // Sync meetingId, managedEmployees, and peers when they change
  useEffect(() => {
    const managedEmployeesSerialized = JSON.stringify(managedEmployees)
    const peersSerialized = JSON.stringify(peers)
    const hasChanged =
      prevValuesRef.current.meetingId !== meetingId ||
      prevValuesRef.current.managedEmployees !== managedEmployeesSerialized ||
      prevValuesRef.current.peers !== peersSerialized

    if (hasChanged) {
      setState({
        meetingId,
        managedEmployees,
        peers,
        platform: 'web',
      })

      prevValuesRef.current = {
        meetingId,
        managedEmployees: managedEmployeesSerialized,
        peers: peersSerialized,
      }
    }
  }, [meetingId, managedEmployees, peers, setState])

  return null
}
